import { type ApolloClient, gql } from '@apollo/client/core';
import type { Mutation, Query, SaveUserAccountMutationInput, VpError } from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import { CONNECTION_METADATA_FIELDS } from '../../connection/Api';
import type { ConnectionArguments } from '../../connection/Api';
import * as GqlConnection from '../../connection/GqlConnection';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as UserAccount from './UserAccount';
import * as UserAccountInput from './UserAccountInput';
import { USER_ACCOUNT_FIELDS } from './fragments';

/**
 * The AppSchema team surface: the account's members plus the three mutations the Team screen
 * drives. All four fields sit on the schema root — the legacy `app { … }` wrappers they replace
 * are deprecated for removal in 6.1.
 *
 * The page size is defaulted because `UserAccountConnection` exposes no `totalCount` and the Team
 * screen has no pagination UI: an unsized call renders whatever the field's own default page is,
 * which at the time of writing is ten members. 100 is the size the legacy read used, and matches
 * the cap the backend declares on the field — neither number is expressible in the SDL, so treat
 * both as the backend's to change.
 */
export const DEFAULT_PAGE_SIZE = 100;

export const USER_ACCOUNTS_QUERY = gql`
  query UserAccounts($after: String, $before: String, $first: Int, $last: Int, $userId: Int) {
    userAccounts(after: $after, before: $before, first: $first, last: $last, userId: $userId) {
      edges {
        cursor
        node {
          ...UserAccountFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${USER_ACCOUNT_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

/**
 * The account comes from the `X-VP-Account-Id` header — unlike the legacy field, this one takes
 * no `accountId`. `network-only` for the reason the legacy read used it: the Team screen refetches
 * after a save or a delete, and a cached list would show the state it just changed.
 *
 * The rows carry `user.active`, which is what tells an accepted invitation from an outstanding one.
 */
export const fetchUserAccounts = async (
  client: ApolloClient<Query>,
  { pagination, userId }: { pagination?: ConnectionArguments; userId?: number },
): Promise<GqlConnection.GqlConnection<UserAccount.UserAccount>> => {
  const paged = pagination ?? {};

  // Assigned rather than spread in conditionally: both keys have to stay *absent*, not
  // present-and-undefined, because an explicitly-undefined variable is a different request.
  const variables: Record<string, unknown> = { ...paged };

  // Only on an unsized forward page. Adding `first` to a `before` page would read the first
  // N of the whole set rather than the page before the cursor, and pairing it with `last`
  // would slice twice.
  if (paged.first === undefined && paged.last === undefined && paged.before === undefined) {
    variables.first = DEFAULT_PAGE_SIZE;
  }

  if (userId !== undefined) {
    variables.userId = userId;
  }

  const { data } = await client.query({
    query: USER_ACCOUNTS_QUERY,
    fetchPolicy: 'network-only',
    variables,
  });

  return UserAccount.fromGqlConnection(data.userAccounts);
};

export const SAVE_USER_ACCOUNT_MUTATION = gql`
  mutation SaveUserAccount($input: SaveUserAccountMutationInput!) {
    saveUserAccount(input: $input) {
      userAccount {
        ...UserAccountFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${USER_ACCOUNT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/**
 * Invites when the input carries no `id`, updates the membership when it does.
 *
 * `SaveUserAccountMutationPayload` has no `success` flag — unlike `saveEvent` — so the outcome is
 * derived from what the payload carries. "Neither errors nor an entity" is indistinguishable from
 * success at the schema level; failing here keeps the message legible instead of handing the Team
 * form a saved state with nothing to show.
 */
export const saveUserAccount = async (
  client: ApolloClient<Mutation>,
  userAccount: UserAccountInput.UserAccountInput,
): Promise<VPResult<UserAccount.UserAccount, VPErrors.VPError[]>> => {
  const input: SaveUserAccountMutationInput = { userAccount: UserAccountInput.toGqlInput(userAccount) };

  const { data } = await client.mutate<Mutation>({ mutation: SAVE_USER_ACCOUNT_MUTATION, variables: { input } });

  const payload = data?.saveUserAccount;

  if (!payload) {
    throw new Error('saveUserAccount mutation returned no data');
  }

  const errors = VPErrors.fromGqlCollection(payload.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload.userAccount) {
    throw new Error('saveUserAccount returned neither a user account nor errors');
  }

  return { success: true, data: UserAccount.fromGql(payload.userAccount) };
};

export const DELETE_USER_ACCOUNT_MUTATION = gql`
  mutation DeleteUserAccount($userAccountId: Int!) {
    deleteUserAccount(input: { userAccountId: $userAccountId }) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const SEND_USER_ACCOUNT_INVITATION_MUTATION = gql`
  mutation SendUserAccountInvitation($userAccountId: Int!) {
    sendUserAccountInvitation(input: { userAccountId: $userAccountId }) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/**
 * `errors` is always an array — empty on success — so callers branch on `success`, not on the
 * error list being present. The legacy payloads returned no key at all when they succeeded.
 */
export type MutationResult = { success: boolean; errors: VPErrors.VPError[] };

/**
 * A missing payload throws for the reason `saveUserAccount` does: `success: false` with nothing in
 * `errors` would put the Team screen in an error state with no message to show.
 */
const toResult = (
  operation: string,
  payload?: { success: boolean; errors?: VpError[] | null } | null,
): MutationResult => {
  if (!payload) {
    throw new Error(`${operation} mutation returned no data`);
  }

  return {
    success: payload.success,
    errors: VPErrors.fromGqlCollection(payload.errors ?? []),
  };
};

export const deleteUserAccount = async (
  client: ApolloClient<Mutation>,
  userAccountId: number,
): Promise<MutationResult> => {
  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_USER_ACCOUNT_MUTATION,
    variables: { userAccountId },
  });

  return toResult('deleteUserAccount', data?.deleteUserAccount);
};

/** Resends the "set your password" invitation to a member who has not accepted theirs. */
export const sendUserAccountInvitation = async (
  client: ApolloClient<Mutation>,
  userAccountId: number,
): Promise<MutationResult> => {
  const { data } = await client.mutate<Mutation>({
    mutation: SEND_USER_ACCOUNT_INVITATION_MUTATION,
    variables: { userAccountId },
  });

  return toResult('sendUserAccountInvitation', data?.sendUserAccountInvitation);
};
